import { useCallback, useEffect, useRef } from "react";
import type { components } from "../../api/generated/survey-svc";
import {
  clearInvitationToken,
  readInvitationToken,
} from "../../auth/invitation-return";
import { useSession } from "../../auth/SessionProvider";
import appConfig from "../../configs/app.config";
import { adaptAuthenticatedUser } from "../../domain/authenticated-user/adapter";
import type { AuthenticatedUser } from "../../domain/authenticated-user/model";
import {
  AUTHENTICATION_RETURN_TO_FALLBACK,
  buildAuthenticationPath,
  resolveAuthenticationReturnTo,
} from "../../router/auth-return-intent";
import { useNavigate } from "../../router/runtime";
import {
  apiCompleteProviderRegistration,
  apiForgotPassword,
  apiInvitationSignUp,
  apiMe,
  apiResetPassword,
  apiSignIn,
  apiSignOut,
  apiSignUp,
  apiTwoFactorAuthentication,
  apiVerifyEmailCode,
} from "../../services/IndicafixAuthService";

type AuthResult = {
  status: "success" | "failed";
  message?: string;
  returnTo?: string;
  statusCode?: number;
  user?: AuthenticatedUser;
};
type SignInValues = components["schemas"]["AuthSigninParams"];
type SignUpValues = components["schemas"]["AuthSignupParams"];
type ProviderRegistrationValues =
  components["schemas"]["AuthProviderRegistrationParams"];
type AuthSession =
  | components["schemas"]["AuthSignin"]
  | components["schemas"]["AuthProviderAuthenticated"]
  | components["schemas"]["AuthTwoFactorAuthenticationSocial"];
type ForgotPasswordValues = components["schemas"]["AuthForgotPasswordParams"];
type ResetPasswordValues = components["schemas"]["AuthResetPasswordParams"];

const failureMessage = (error: unknown): string => {
  if (typeof error === "object" && error !== null && "error" in error) {
    const message = error.error;
    if (typeof message === "string") return message;
  }
  return String(error);
};

function useAuth() {
  const navigate = useNavigate();
  const session = useSession();
  const registration = useRef({ mounted: true, generation: 0 });
  useEffect(() => {
    registration.current.mounted = true;
    return () => {
      registration.current.mounted = false;
      registration.current.generation++;
    };
  }, []);
  const beginRegistrationAttempt = useCallback(
    (invitationToken: string | null) => {
      const generation = ++registration.current.generation;
      const href = window.location.href;
      const navigationKey = window.history.state?.__TSR_key;
      return () =>
        registration.current.mounted &&
        registration.current.generation === generation &&
        window.location.href === href &&
        window.history.state?.__TSR_key === navigationKey &&
        readInvitationToken() === invitationToken;
    },
    [],
  );

  const handleUserAuthenticated = useCallback(
    (data: AuthSession) => {
      session.authenticate(data.accessToken, adaptAuthenticatedUser(data.user));
    },
    [session],
  );

  const signIn = useCallback(
    async (
      values: SignInValues,
      requestedReturnTo: unknown = AUTHENTICATION_RETURN_TO_FALLBACK,
    ): Promise<AuthResult> => {
      const returnTo = resolveAuthenticationReturnTo(requestedReturnTo);
      const result = await apiSignIn(values);
      if (result.status !== "success")
        return { status: "failed", message: failureMessage(result.error) };
      if (!result.data) {
        session.setPendingEmail(values.email);
        navigate(
          buildAuthenticationPath(appConfig.twoFactorAuthentication, returnTo),
          { replace: true },
        );
      } else {
        handleUserAuthenticated(result.data);
        navigate(returnTo, { replace: true });
      }
      return { status: "success", message: "" };
    },
    [handleUserAuthenticated, navigate, session],
  );

  const twoFactorAuthentication = useCallback(
    async (
      token: string,
      requestedReturnTo: unknown = AUTHENTICATION_RETURN_TO_FALLBACK,
      challengeToken?: string,
    ): Promise<AuthResult> => {
      const returnTo = resolveAuthenticationReturnTo(requestedReturnTo);
      const result = await apiTwoFactorAuthentication(
        challengeToken
          ? { challengeToken, token }
          : { email: session.pendingEmail, token },
      );
      if (result.status === "success" && result.data) {
        handleUserAuthenticated(result.data as AuthSession);
        const authenticatedReturnTo =
          "returnTo" in result.data
            ? resolveAuthenticationReturnTo(result.data.returnTo)
            : returnTo;
        navigate(authenticatedReturnTo, { replace: true });
        return { status: "success", message: "" };
      }
      return {
        status: "failed",
        message:
          result.status === "success"
            ? "Missing session"
            : failureMessage(result.error),
      };
    },
    [handleUserAuthenticated, navigate, session],
  );

  const signInWithEmailCode = useCallback(
    async (
      values: components["schemas"]["AuthEmailCodeVerifyParams"],
      requestedReturnTo: unknown,
    ): Promise<AuthResult> => {
      const result = await apiVerifyEmailCode(values);
      if (result.status !== "success" || !result.data)
        return { status: "failed" };
      handleUserAuthenticated(result.data);
      navigate(resolveAuthenticationReturnTo(requestedReturnTo), {
        replace: true,
      });
      return { status: "success" };
    },
    [handleUserAuthenticated, navigate],
  );

  const signUp = useCallback(
    async (values: SignUpValues): Promise<AuthResult> => {
      const invitationToken = readInvitationToken();
      const isCurrent = beginRegistrationAttempt(invitationToken);
      let result: Awaited<ReturnType<typeof apiSignUp>>;
      if (invitationToken) {
        const {
          company: _company,
          acceptTerms: _acceptTerms,
          ...account
        } = values as SignUpValues & { acceptTerms?: boolean };
        result = await apiInvitationSignUp({ ...account, invitationToken });
      } else result = await apiSignUp(values);
      if (!isCurrent()) return { status: "failed", message: "" };
      if (result.status === "success" && result.data) {
        handleUserAuthenticated(result.data as AuthSession);
        navigate(
          invitationToken
            ? "/company-invitation"
            : appConfig.authenticatedEntryPath,
        );
        return { status: "success", message: "" };
      }
      if (
        invitationToken &&
        result.status === "http-error" &&
        result.statusCode === 400 &&
        failureMessage(result.error) === "Invitation unavailable"
      ) {
        clearInvitationToken();
        navigate("/company-invitation", { replace: true });
      }
      return {
        status: "failed",
        message:
          result.status === "success"
            ? "Missing session"
            : failureMessage(result.error),
      };
    },
    [beginRegistrationAttempt, handleUserAuthenticated, navigate],
  );

  const completeProviderRegistration = useCallback(
    async (values: ProviderRegistrationValues): Promise<AuthResult> => {
      const isCurrent = beginRegistrationAttempt(readInvitationToken());
      const result = await apiCompleteProviderRegistration(values);
      if (!isCurrent()) return { status: "failed", message: "" };
      if (result.status === "success" && result.data) {
        handleUserAuthenticated(result.data);
        return {
          status: "success",
          message: "",
          returnTo: resolveAuthenticationReturnTo(result.data.returnTo),
        };
      }

      if (
        values.invitationToken &&
        result.status === "http-error" &&
        result.statusCode === 400 &&
        failureMessage(result.error) === "Invitation unavailable"
      ) {
        clearInvitationToken();
        navigate("/company-invitation", { replace: true });
      }
      return {
        status: "failed",
        ...(result.status === "http-error"
          ? { statusCode: result.statusCode }
          : {}),
        message:
          result.status === "success"
            ? "Missing session"
            : failureMessage(result.error),
      };
    },
    [beginRegistrationAttempt, handleUserAuthenticated, navigate],
  );

  const signOut = useCallback(async (): Promise<AuthResult> => {
    const signOutRequest = apiSignOut();
    session.clear();
    navigate(appConfig.unAuthenticatedEntryPath);

    const result = await signOutRequest;
    if (result.status === "success") {
      return { status: "success", message: "" };
    }
    return { status: "failed", message: failureMessage(result.error) };
  }, [navigate, session]);

  const forgotPasswordCreate = useCallback(
    async (values: ForgotPasswordValues): Promise<AuthResult> => {
      const result = await apiForgotPassword(values);
      return result.status === "success"
        ? { status: "success", message: "" }
        : { status: "failed", message: failureMessage(result.error) };
    },
    [],
  );

  const forgotPasswordReset = useCallback(
    async (values: ResetPasswordValues): Promise<AuthResult> => {
      const result = await apiResetPassword(values);
      return result.status === "success"
        ? { status: "success", message: "" }
        : { status: "failed", message: failureMessage(result.error) };
    },
    [],
  );

  const getMe = useCallback(async (): Promise<AuthResult> => {
    const expectedToken = session.token;
    if (!expectedToken) {
      return { status: "failed", message: "Missing session" };
    }

    const result = await apiMe();
    if (result.status === "success" && result.data) {
      const user = adaptAuthenticatedUser(
        (result.data as components["schemas"]["AuthMe"]).currentUser,
      );
      session.updateUser(user, expectedToken);
      return { status: "success", message: "", user };
    }
    return {
      status: "failed",
      message:
        result.status === "success"
          ? "Missing user"
          : failureMessage(result.error),
    };
  }, [session]);

  return {
    authenticated: session.status === "authenticated",
    restoring: session.status === "restoring",
    signIn,
    signInWithEmailCode,
    signUp,
    completeProviderRegistration,
    signOut,
    forgotPasswordCreate,
    forgotPasswordReset,
    getMe,
    twoFactorAuthentication,
  };
}

export default useAuth;
